import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AnimatedSection } from "@/components/AnimatedSection";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { 
  Target, TrendingUp, Calendar, Lightbulb, Zap, MessageSquare,
  User, Sparkles, LayoutDashboard, ArrowRight, CheckCircle2
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ReactGA from "react-ga4";

const isProduction = import.meta.env.PROD ||
  window.location.hostname === "socialmedia-a-team.com" ||
  window.location.hostname === "www.socialmedia-a-team.com";

const trackGAEvent = (action: string, label?: string, params?: Record<string, any>) => {
  if (isProduction) {
    ReactGA.gtag("event", action, {
      event_category: "engagement",
      event_label: label || "vsl_page",
      ...params
    });
  }
};

import agent1 from "@assets/Gemini_Generated_Image_jp7nmijp7nmijp7n (1)_1760809993862.png";
import agent2 from "@assets/Gemini_Generated_Image_vnvxzyvnvxzyvnvx (1)_1760810006394.png";
import agent3 from "@assets/Gemini_Generated_Image_3pwcq03pwcq03pwc_1760810036453.png";
import agent4 from "@assets/Gemini_Generated_Image_akydq1akydq1akyd_1760810088993.png";
import vslVideo from "@assets/A_Team_VSLkomp_1765798388103.mp4";

// Waitlist paused (Mike 2026-10-02): the founding programme is bought directly, so the VSL CTA leads to sign-up.
function FoundingCta({ compact = false }: { compact?: boolean }) {
  return (
    <div className="text-center space-y-3" data-testid="founding-cta">
      <h2 className={compact ? "text-lg sm:text-xl font-bold font-playfair" : "text-xl sm:text-2xl font-bold font-playfair"}>
        Gründungsprogramm für Hotels
      </h2>
      <p className="text-sm text-muted-foreground">
        249 € netto pro Monat zzgl. USt., Einrichtung entfällt, monatlich kündbar.
      </p>
      <a
        href="/login?tab=register&src=vsl"
        className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        data-testid="link-founding-start"
      >
        Gründungsprogramm starten
      </a>
      <p className="text-xs text-muted-foreground">
        Start-Garantie: Ist Ihr Social Media A-Team nicht innerhalb von 7 Tagen startklar, ist Ihr erster Monat kostenlos.
      </p>
    </div>
  );
}

export default function VideoLanding() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPriceBox, setShowPriceBox] = useState(false);
  const [hasTrackedVideoPlay, setHasTrackedVideoPlay] = useState(false);
  const [trackedProgress, setTrackedProgress] = useState({ p25: false, p50: false, p75: false });
  const [hasTrackedComplete, setHasTrackedComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gdprConsent: false
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);

  // Get referrer slug from sessionStorage (set by preview page CTA)
  const getReferrerSlug = (): string | undefined => {
    try {
      return sessionStorage.getItem("preview_referrer") || undefined;
    } catch {
      return undefined;
    }
  };

  // Track VSL page view on mount
  useEffect(() => {
    const trackPageView = async () => {
      try {
        const referrerSlug = getReferrerSlug();
        await fetch("/api/vsl/view", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ referrerSlug }),
        });
        // Clear referrer after recording to avoid stale attribution
        if (referrerSlug) {
          try {
            sessionStorage.removeItem("preview_referrer");
          } catch {
            // Ignore sessionStorage errors
          }
        }
      } catch (error) {
        console.error("Failed to track VSL page view:", error);
      }
    };
    trackPageView();
  }, []);

  // Track video play (only once per session)
  const trackVideoPlay = async () => {
    if (hasTrackedVideoPlay) return;
    setHasTrackedVideoPlay(true);
    
    try {
      const referrerSlug = getReferrerSlug();
      await fetch("/api/vsl/video-play", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referrerSlug }),
      });
    } catch (error) {
      console.error("Failed to track VSL video play:", error);
    }
  };

  const handleVideoPlay = () => {
    trackVideoPlay();
    trackGAEvent("video_play");
  };

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.currentTime >= 153) {
      setShowPriceBox(true);
    }
    
    const duration = video.duration;
    if (duration > 0) {
      const percent = (video.currentTime / duration) * 100;
      
      if (percent >= 25 && !trackedProgress.p25) {
        setTrackedProgress(prev => ({ ...prev, p25: true }));
        trackGAEvent("video_progress", "vsl_page", { percent: 25 });
      }
      if (percent >= 50 && !trackedProgress.p50) {
        setTrackedProgress(prev => ({ ...prev, p50: true }));
        trackGAEvent("video_progress", "vsl_page", { percent: 50 });
      }
      if (percent >= 75 && !trackedProgress.p75) {
        setTrackedProgress(prev => ({ ...prev, p75: true }));
        trackGAEvent("video_progress", "vsl_page", { percent: 75 });
      }
    }
  };

  const handleVideoEnded = () => {
    if (!hasTrackedComplete) {
      setHasTrackedComplete(true);
      trackGAEvent("video_complete");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.gdprConsent) {
      toast({
        title: "Bitte alle Felder ausfüllen",
        description: "Name, E-Mail und Datenschutz-Zustimmung sind erforderlich.",
        variant: "destructive"
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Ungültige E-Mail",
        description: "Bitte gib eine gültige E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    trackGAEvent("waitlist_submit");
    
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          gdprConsent: formData.gdprConsent,
          source: "vsl-landing"
        })
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        trackGAEvent("waitlist_confirmed");
        toast({
          title: "Du bist auf der Liste!",
          description: "Check deine E-Mails."
        });
      } else {
        // Handle specific error types from backend with fallback messages
        if (data.error === 'duplicate') {
          toast({
            title: "E-Mail bereits registriert",
            description: data.message || "Diese E-Mail ist bereits auf der Warteliste.",
            variant: "destructive"
          });
        } else if (data.error === 'invalid_data') {
          toast({
            title: "Ungültige Daten",
            description: data.message || "Bitte überprüfe deine Eingaben.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Fehler beim Absenden",
            description: data.message || "Bitte versuche es später erneut.",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      toast({
        title: "Fehler beim Absenden",
        description: "Bitte versuche es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const agents = [
    { 
      id: 1, 
      image: agent1, 
      name: "LINA",
      role: "Die Strategin",
      tagline: "Plant, koordiniert und behält den Überblick.",
      personality: [
        { label: "Coffee", value: "Americano am Morgen, grüner Tee am Abend" },
        { label: "Talent", value: "Sieht Trends 48h bevor sie viral gehen" },
        { label: "Quirk", value: "Plant ihren Urlaub wie Content-Kalender" },
        { label: "Hobby", value: "Studiert erfolgreiche Werbekampagnen der 60er-80er" },
        { label: "Fun Fact", value: "Content-Strategie auf Emoji-Basis - funktionierte perfekt" }
      ],
      skills: [
        { icon: Target, color: "text-green-500", label: "Content Strategy" },
        { icon: Lightbulb, color: "text-yellow-500", label: "Editorial Calendar" },
        { icon: TrendingUp, color: "text-blue-500", label: "Trend Analysis" },
        { icon: Zap, color: "text-orange-500", label: "Competitive Intelligence" },
        { icon: Calendar, color: "text-purple-500", label: "Campaign Planning" }
      ],
      signature: "Ich liebe es, wenn eine Content-Strategie aufgeht."
    },
    { 
      id: 2, 
      image: agent2, 
      name: "ALEX",
      role: "Der Analyst",
      tagline: "Weiß, was funktioniert - und was nicht.",
      personality: [
        { label: "Coffee", value: "Cold Brew - effizient, datiert nicht" },
        { label: "Talent", value: "Findet Muster, wo andere Chaos sehen" },
        { label: "Quirk", value: "Trackt eigenen Koffein-Konsum in Echtzeit (in Excel)" },
        { label: "Hobby", value: "Spielt Schach gegen KI (und analysiert die Daten)" },
        { label: "Fun Fact", value: "Content-Strategie nur auf Wetter-Prognosen - 40% mehr Engagement" }
      ],
      skills: [
        { icon: TrendingUp, color: "text-blue-500", label: "Performance Analytics" },
        { icon: Target, color: "text-red-500", label: "A/B Testing" },
        { icon: Calendar, color: "text-purple-500", label: "Data Viz" },
        { icon: Lightbulb, color: "text-amber-500", label: "Voice-to-Insights" }
      ],
      signature: "Die Daten fliegen mir zu!"
    },
    { 
      id: 3, 
      image: agent3, 
      name: "EMMA",
      role: "Die Kreative",
      tagline: "Macht aus Bildern Content, der auffällt.",
      personality: [
        { label: "Coffee", value: "Flat White - perfekte Balance, wie guter Content" },
        { label: "Talent", value: "Sieht in jedem Foto 12 verschiedene Stories" },
        { label: "Quirk", value: "Beurteilt Menschen nach ihrer Instagram-Ästhetik" },
        { label: "Hobby", value: "Fotografiert Architektur-Details" },
        { label: "Fun Fact", value: "Aus einem Hotel-Lobby-Foto Content für 2 Wochen generieren" }
      ],
      skills: [
        { icon: Lightbulb, color: "text-pink-500", label: "Photo-to-Content AI" },
        { icon: Target, color: "text-indigo-500", label: "Visual Analysis" },
        { icon: Zap, color: "text-cyan-500", label: "Behind-the-Scenes" },
        { icon: Calendar, color: "text-emerald-500", label: "Multi-Format Visuals" }
      ],
      signature: "Ich bemitleide jeden, der ohne gute Visuals postet."
    },
    { 
      id: 4, 
      image: agent4, 
      name: "MAX",
      role: "Der Texter",
      tagline: "Schreibt Posts, die nach dir klingen.",
      personality: [
        { label: "Coffee", value: "Doppelter Espresso, doppelte Wirkung" },
        { label: "Talent", value: "Verkauft Eis an Eskimos - mit 7 Worten" },
        { label: "Quirk", value: "Korrigiert Speisekarten in Restaurants" },
        { label: "Hobby", value: "Sammelt Headlines aus den 90ern" },
        { label: "Fun Fact", value: "LinkedIn-Post mit nur Emojis - 50k Views" }
      ],
      skills: [
        { icon: MessageSquare, color: "text-blue-500", label: "Copywriting" },
        { icon: Target, color: "text-green-500", label: "Hook Writing" },
        { icon: Lightbulb, color: "text-yellow-500", label: "Storytelling" },
        { icon: Zap, color: "text-orange-500", label: "CTA Optimization" }
      ],
      signature: "Jedes Wort verdient seinen Platz."
    }
  ];

  const benefits = [
    {
      icon: User,
      title: "Dein Voice Profile",
      description: "Wir analysieren dein Unternehmen und erstellen ein Profil, das deine Stimme perfekt trifft. Jeder Post klingt nach dir - ohne dass du ihn schreiben musst."
    },
    {
      icon: Sparkles,
      title: "Content in Sekunden",
      description: "Foto hochladen, Posts bekommen. So einfach ist das. Emma, Max und das Team verwandeln deine Bilder in fertigen Content für alle Plattformen."
    },
    {
      icon: LayoutDashboard,
      title: "Ein Dashboard für alles",
      description: "Alle Posts, alle Plattformen, alle Analysen an einem Ort. Kein Wechseln zwischen Apps. Kein Chaos. Nur Überblick."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      {/* SECTION 1: HERO with Video + Waitlist Side by Side */}
      <section className="px-4 py-8 lg:py-12">
        <div className="container max-w-6xl mx-auto">
          {/* Brand */}
          <AnimatedSection direction="up" className="text-center mb-6">
            <div className="flex items-center justify-center gap-2">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xl font-playfair">A</span>
              </div>
              <span className="text-xl font-bold text-primary font-playfair">Social Media A-Team</span>
            </div>
          </AnimatedSection>

          {/* Headline */}
          <AnimatedSection delay={100} direction="up" className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-playfair leading-tight">
              Du hasst Social Media.{" "}
              <span className="text-primary">Dein A-Team liebt es.</span>
            </h1>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-medium">
              Vier KI-Agenten. Null Ausreden. Fertige Posts.
            </p>
          </AnimatedSection>

          {/* Video + Form Overlapping Layout */}
          {/* Mobile: Stack vertically */}
          <div className="lg:hidden flex flex-col gap-6 items-center">
            {/* Video Player - Mobile */}
            <AnimatedSection delay={200} direction="up">
              <div className="w-[280px] sm:w-[300px] max-w-full mx-auto">
                <div className="relative w-full rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-black" style={{ aspectRatio: '9/16' }}>
                  <video
                    ref={mobileVideoRef}
                    controls
                    playsInline
                    autoPlay
                    muted
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={handleVideoPlay}
                    onEnded={handleVideoEnded}
                    className="w-full h-full object-cover"
                    style={{ display: 'block' }}
                    data-testid="video-player-mobile"
                  >
                    <source src={vslVideo} type="video/mp4" />
                    Dein Browser unterstützt das Video-Tag nicht.
                  </video>
                </div>
              </div>
            </AnimatedSection>

            {/* Waitlist Form - Mobile */}
            <AnimatedSection delay={300} direction="up">
              <Card className="shadow-xl w-full max-w-md">
                <CardContent className="p-6 space-y-4">
                  <FoundingCta />
                </CardContent>
              </Card>
              {showPriceBox && (
                <Card className="shadow-xl mt-4 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="card-price-mobile">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex -space-x-2">
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent1} alt="Lina" />
                        <AvatarFallback>L</AvatarFallback>
                      </Avatar>
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent2} alt="Alex" />
                        <AvatarFallback>A</AvatarFallback>
                      </Avatar>
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent3} alt="Emma" />
                        <AvatarFallback>E</AvatarFallback>
                      </Avatar>
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent4} alt="Max" />
                        <AvatarFallback>M</AvatarFallback>
                      </Avatar>
                    </div>
                    <div>
                      <p className="font-bold text-sm" data-testid="text-price-title-mobile">Social Media A-Team, Monatspreis</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-primary" data-testid="text-price-current-mobile">€249</span>
                        <span className="text-sm text-muted-foreground" data-testid="text-price-unit-mobile">pro Monat, zzgl. MwSt.</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </AnimatedSection>
          </div>

          {/* Desktop: Side by Side Layout - NO OVERLAP */}
          <div className="hidden lg:flex lg:justify-center lg:items-center lg:gap-8 xl:gap-12 lg:px-4">
            {/* Video Player - Fixed size, left side */}
            <AnimatedSection delay={200} direction="left" className="flex-shrink-0">
              <div className="w-[300px] xl:w-[320px]">
                <div className="relative aspect-[9/16] rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-muted">
                  <video
                    ref={videoRef}
                    controls
                    playsInline
                    autoPlay
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={handleVideoPlay}
                    onEnded={handleVideoEnded}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="video-player"
                  >
                    <source src={vslVideo} type="video/mp4" />
                    Dein Browser unterstützt das Video-Tag nicht.
                  </video>
                </div>
              </div>
            </AnimatedSection>

            {/* Waitlist Form - Right side, no overlap */}
            <AnimatedSection delay={300} direction="right" className="flex-shrink-0 w-full max-w-[340px]">
              <Card className="shadow-2xl">
                <CardContent className="p-5 space-y-3">
                  <FoundingCta compact />
                </CardContent>
              </Card>
              {showPriceBox && (
                <Card className="shadow-xl mt-4 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="card-price-desktop">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex -space-x-2">
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent1} alt="Lina" />
                        <AvatarFallback>L</AvatarFallback>
                      </Avatar>
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent2} alt="Alex" />
                        <AvatarFallback>A</AvatarFallback>
                      </Avatar>
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent3} alt="Emma" />
                        <AvatarFallback>E</AvatarFallback>
                      </Avatar>
                      <Avatar className="w-10 h-10 border-2 border-background">
                        <AvatarImage src={agent4} alt="Max" />
                        <AvatarFallback>M</AvatarFallback>
                      </Avatar>
                    </div>
                    <div>
                      <p className="font-bold text-sm" data-testid="text-price-title-desktop">Social Media A-Team, Monatspreis</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-primary" data-testid="text-price-current-desktop">€249</span>
                        <span className="text-sm text-muted-foreground" data-testid="text-price-unit-desktop">pro Monat, zzgl. MwSt.</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SECTION 2: A-Team Introduction with FlipCards */}
      <section className="py-8 lg:py-12 bg-card/50">
        <div className="container px-4 lg:px-8 max-w-7xl mx-auto">
          <AnimatedSection direction="up" className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-playfair">
              Vier Spezialisten. <span className="text-primary">Ein Ziel: Dein Content.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Hover über die Karten, um mehr zu erfahren.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {agents.map((agent, index) => (
              <AnimatedSection 
                key={agent.id} 
                delay={100 + index * 100} 
                direction="up"
                className="h-full"
              >
                <div className="space-y-2" data-testid={`agent-wrapper-${index}`}>
                  <h3 className="text-center font-playfair font-bold text-lg text-foreground">
                    {agent.name} - {agent.role}
                  </h3>
                  <div 
                    className="flip-card-container group cursor-pointer"
                    style={{ perspective: '1000px' }}
                    data-testid={`agent-card-${index}`}
                  >
                    <div className="flip-card-inner relative w-full aspect-[3/4] transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                      {/* Front Side */}
                      <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] rounded-lg overflow-hidden bg-card border shadow-lg">
                        <img
                          src={agent.image}
                          alt={`${agent.name} - ${agent.role}`}
                          className="w-full h-full object-cover"
                          data-testid={`agent-img-${index}`}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                          <p className="text-white text-sm font-medium">{agent.tagline}</p>
                        </div>
                      </div>
                      
                      {/* Back Side */}
                      <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg overflow-hidden bg-card border shadow-lg">
                        <div className="w-full h-full p-3 flex flex-col justify-between">
                          <div className="space-y-2.5">
                            {/* Persönlichkeit */}
                            <div className="space-y-1">
                              <h4 className="font-bold text-sm text-foreground">Persönlichkeit</h4>
                              <div className="space-y-1">
                                {agent.personality.map((trait, i) => (
                                  <p key={i} className="text-[10px] leading-tight text-foreground">
                                    <span className="font-semibold">{trait.label}:</span> {trait.value}
                                  </p>
                                ))}
                              </div>
                            </div>
                            
                            {/* Kern-Fähigkeiten */}
                            <div className="space-y-1">
                              <h4 className="font-bold text-sm text-foreground">Kern-Fähigkeiten</h4>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                {agent.skills.map((skill, i) => (
                                  <div key={i} className="flex items-start gap-1.5">
                                    <skill.icon className={`h-3 w-3 ${skill.color} mt-0.5 flex-shrink-0`} />
                                    <span className="text-[10px] leading-tight text-foreground">{skill.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          {/* Signature Message */}
                          <div className="flex items-start gap-2 pt-1.5 border-t border-border/40">
                            <MessageSquare className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                            <p className="text-xs italic text-foreground leading-tight">
                              <span className="font-semibold">{agent.name}:</span> "{agent.signature}"
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Benefits */}
      <section className="py-8 lg:py-12">
        <div className="container px-4 lg:px-8 max-w-5xl mx-auto">
          <AnimatedSection direction="up" className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-playfair">
              Ein System. <span className="text-primary">Null Aufwand.</span>
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={100 + index * 100} direction="up">
                <Card className="h-full hover-elevate">
                  <CardContent className="p-5 space-y-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <benefit.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-bold text-base font-playfair">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/40 py-6">
        <div className="container px-4 lg:px-8">
          <div className="flex flex-col gap-4 items-center">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm font-playfair">A</span>
              </div>
              <span className="font-semibold text-primary font-playfair text-sm">Social Media A-Team</span>
            </div>
            <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
              <span>© 2025 SumoBrands</span>
              <span>•</span>
              <Link href="/impressum" className="hover:text-primary hover:underline" data-testid="link-impressum">
                Impressum
              </Link>
              <Link href="/datenschutz" className="hover:text-primary hover:underline" data-testid="link-datenschutz">
                Datenschutz
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
